import { Eye, EyeOff, GripVertical } from 'lucide-react';
import React from 'react';
import type { PdfTemplateSection, SectionDefinition } from '../../types/pdfTemplate';

interface PdfSectionManagerProps {
  sections: PdfTemplateSection[];
  availableSections: SectionDefinition[];
  onChange: (sections: PdfTemplateSection[]) => void;
}

const PdfSectionManager: React.FC<PdfSectionManagerProps> = ({
  sections,
  availableSections,
  onChange,
}) => {
  const sortedSections = [...sections].sort((a, b) => a.order - b.order);

  const toggleVisibility = (key: string) => {
    const availableDef = availableSections.find(s => s.key === key);
    if (availableDef?.required) return;

    onChange(sections.map(s => (s.key === key ? { ...s, visible: !s.visible } : s)));
  };

  const updateCustomLabel = (key: string, customLabel: string) => {
    onChange(
      sections.map(s => (s.key === key ? { ...s, customLabel: customLabel || undefined } : s))
    );
  };

  const moveUp = (index: number) => {
    if (index <= 0) return;
    const newSections = [...sortedSections];
    const temp = newSections[index - 1];
    newSections[index - 1] = newSections[index];
    newSections[index] = temp;
    onChange(newSections.map((s, i) => ({ ...s, order: i + 1 })));
  };

  const moveDown = (index: number) => {
    if (index >= sortedSections.length - 1) return;
    const newSections = [...sortedSections];
    const temp = newSections[index + 1];
    newSections[index + 1] = newSections[index];
    newSections[index] = temp;
    onChange(newSections.map((s, i) => ({ ...s, order: i + 1 })));
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-sm font-semibold text-gray-900">Section Manager</h3>
        <p className="text-xs text-gray-500 mt-1">
          Toggle visibility and reorder PDF sections. Required sections cannot be hidden.
        </p>
      </div>

      <div className="space-y-2">
        {sortedSections.map((section, index) => {
          const availableDef = availableSections.find(s => s.key === section.key);
          const isRequired = availableDef?.required;

          return (
            <div
              key={section.key}
              className={`flex items-center space-x-3 p-3 rounded-lg border transition-colors ${
                section.visible ? 'bg-white border-gray-200' : 'bg-gray-50 border-gray-100'
              }`}
            >
              {/* Drag handle / Order controls */}
              <div className="flex flex-col items-center space-y-0.5">
                <button
                  onClick={() => moveUp(index)}
                  disabled={index === 0}
                  className="p-0.5 hover:bg-gray-200 rounded disabled:opacity-30"
                >
                  <svg className="h-3 w-3 text-gray-400" viewBox="0 0 12 12">
                    <path d="M6 3L2 7h8L6 3z" fill="currentColor" />
                  </svg>
                </button>
                <GripVertical className="h-4 w-4 text-gray-300" />
                <button
                  onClick={() => moveDown(index)}
                  disabled={index === sortedSections.length - 1}
                  className="p-0.5 hover:bg-gray-200 rounded disabled:opacity-30"
                >
                  <svg className="h-3 w-3 text-gray-400" viewBox="0 0 12 12">
                    <path d="M6 9l4-4H2l4 4z" fill="currentColor" />
                  </svg>
                </button>
              </div>

              {/* Section info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center space-x-2">
                  <span
                    className={`text-sm font-medium ${section.visible ? 'text-gray-900' : 'text-gray-400'}`}
                  >
                    {section.label}
                  </span>
                  {isRequired && (
                    <span className="text-xs bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded">
                      Required
                    </span>
                  )}
                  <span className="text-xs text-gray-400">#{section.order}</span>
                </div>
                {availableDef?.description && (
                  <p className="text-xs text-gray-400 mt-0.5">{availableDef.description}</p>
                )}
              </div>

              {/* Custom label input */}
              <input
                type="text"
                value={section.customLabel || ''}
                onChange={e => updateCustomLabel(section.key, e.target.value)}
                placeholder="Custom label"
                className="w-40 px-2 py-1 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-brand-500 focus:border-brand-500"
              />

              {/* Visibility toggle */}
              <button
                onClick={() => toggleVisibility(section.key)}
                disabled={isRequired}
                className={`p-1.5 rounded transition-colors ${
                  isRequired
                    ? 'text-gray-300 cursor-not-allowed'
                    : section.visible
                      ? 'text-green-600 hover:bg-green-50'
                      : 'text-gray-400 hover:bg-gray-100'
                }`}
              >
                {section.visible ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
              </button>
            </div>
          );
        })}
      </div>

      {sortedSections.length === 0 && (
        <div className="text-center py-8 text-sm text-gray-500">
          No sections configured for this template
        </div>
      )}
    </div>
  );
};

export default PdfSectionManager;
